/**
 * TimeField — custom picker that matches the design-system
 * `.pop` + `.tpick` spec (Tier 2c Time picker):
 *
 *   - form-field trigger with clock icon + mono "09:00 AM" + chevron
 *   - on click, opens QHS Popover with a .tpick body:
 *       - large mono display "09 : 00 AM"
 *       - three scrollable columns (hours / minutes / meridiem)
 *       - active cell is solid brand / white — per design tokens
 *
 * No MUI action bar, no OK/Cancel — each column tap commits the
 * value immediately (matches the Tier 2 "click → applied → closes"
 * rule for single-value pickers).
 *
 * Value contract (unchanged from legacy):
 *   - `minutes`: integer minutes-since-midnight, or `'invalid_value'`
 *   - `onMinutesChange(next)` fires after each column change.
 */
import { useEffect, useRef, useState } from 'react';
import { Popover, styled } from '@shared/ui';

const INVALID = 'invalid_value';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, 10, ..., 55
const MERIDIEMS = ['AM', 'PM'];

const pad = n => (n < 10 ? `0${n}` : String(n));

const parseMinutes = minutes => {
  if (minutes == null || minutes === INVALID) {
    return { hour: 9, minute: 0, meridiem: 'AM', valid: false };
  }
  const n = Number(minutes);
  if (Number.isNaN(n)) return { hour: 9, minute: 0, meridiem: 'AM', valid: false };
  const total = Math.max(0, Math.min(24 * 60 - 1, n));
  const h24 = Math.floor(total / 60);
  const m = total % 60;
  const meridiem = h24 >= 12 ? 'PM' : 'AM';
  let hour = h24 % 12;
  if (hour === 0) hour = 12;
  return { hour, minute: m, meridiem, valid: true };
};

const serialize = ({ hour, minute, meridiem }) => {
  let h = hour % 12;
  if (meridiem === 'PM') h += 12;
  return h * 60 + minute;
};

const TriggerButton = styled('button', {
  shouldForwardProp: p => p !== '$open' && p !== '$disabled' && p !== '$error',
})(({ $open, $disabled, $error }) => ({
  width: '100%',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px 10px 14px',
  fontFamily: 'inherit',
  fontSize: 13,
  color: $disabled ? 'var(--qhs-text-3)' : 'var(--qhs-text-1)',
  background: $disabled ? 'var(--qhs-bg)' : 'var(--qhs-surface)',
  border: `1.5px solid ${
    $error ? 'var(--qhs-error, #dc1212)' : $open ? 'var(--qhs-brand)' : 'var(--qhs-border)'
  }`,
  borderRadius: 'var(--qhs-r-md)',
  cursor: $disabled ? 'not-allowed' : 'pointer',
  textAlign: 'left',
  outline: 'none',
  boxShadow: $open ? '0 0 0 3px var(--qhs-focus-ring)' : 'none',
  transition:
    'border-color var(--qhs-dur-base) var(--qhs-ease), box-shadow var(--qhs-dur-base) var(--qhs-ease)',
  '&:hover:not(:disabled)': {
    borderColor: $open ? 'var(--qhs-brand)' : 'var(--qhs-border-strong)',
  },
  '& svg.clock': { color: 'var(--qhs-text-3)', flexShrink: 0 },
  '& .value': {
    flex: 1,
    fontFamily: 'var(--qhs-font-mono)',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '0.3px',
  },
  '& .value.placeholder': { color: 'var(--qhs-text-3)', fontFamily: 'inherit' },
  '& svg.chev': {
    color: $open ? 'var(--qhs-brand)' : 'var(--qhs-text-3)',
    flexShrink: 0,
    transform: $open ? 'rotate(180deg)' : 'none',
    transition: 'transform 150ms ease',
  },
}));

const Tpick = styled('div')({
  padding: 12,
  width: 240,
});

const TpickDisplay = styled('div')({
  fontFamily: 'var(--qhs-font-mono)',
  fontSize: 24,
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
  padding: '8px 0 14px',
  color: 'var(--qhs-text-1)',
  fontWeight: 600,
  letterSpacing: 1,
});

const TpickCols = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: 6,
  height: 180,
});

const TpickCol = styled('div')({
  border: '1px solid var(--qhs-border)',
  borderRadius: 6,
  overflowY: 'auto',
  textAlign: 'center',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-thumb': {
    background: 'var(--qhs-border-strong)',
    borderRadius: 2,
  },
});

const TpickCell = styled('button', {
  shouldForwardProp: p => p !== '$active',
})(({ $active }) => ({
  display: 'block',
  width: '100%',
  padding: '6px 0',
  fontFamily: 'var(--qhs-font-mono)',
  fontSize: 13,
  cursor: 'pointer',
  color: $active ? '#fff' : 'var(--qhs-text-2)',
  fontVariantNumeric: 'tabular-nums',
  background: $active ? 'var(--qhs-brand)' : 'transparent',
  fontWeight: $active ? 600 : 400,
  border: 'none',
  transition: 'background-color 120ms ease, color 120ms ease',
  '&:hover': {
    background: $active ? 'var(--qhs-brand)' : 'var(--qhs-surface-hover)',
    color: $active ? '#fff' : 'var(--qhs-text-1)',
  },
  '&:focus-visible': { outline: '2px solid var(--qhs-brand)', outlineOffset: -2 },
}));

const ClockIcon = () => (
  <svg
    className="clock"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const ChevIcon = () => (
  <svg
    className="chev"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const FieldError = styled('p')({
  margin: '4px 0 0',
  fontSize: 12,
  color: 'var(--qhs-error, #dc1212)',
  lineHeight: 1.4,
});

const TimeField = ({ minutes, onMinutesChange, disabled, error }) => {
  const parsed = parseMinutes(minutes);
  const [open, setOpen] = useState(false);
  const hoursColRef = useRef(null);
  const minutesColRef = useRef(null);

  // When the popover opens, scroll the active hour and minute cells
  // into view so the picker feels oriented like the legacy one.
  useEffect(() => {
    if (!open) return;
    const scrollActive = container => {
      const active = container?.querySelector('[data-active="true"]');
      if (active) active.scrollIntoView({ block: 'center' });
    };
    // Wait one frame for the popover to mount.
    const id = requestAnimationFrame(() => {
      scrollActive(hoursColRef.current);
      scrollActive(minutesColRef.current);
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const emit = next => onMinutesChange(serialize(next));
  const setHour = h => emit({ ...parsed, hour: h });
  const setMinute = m => emit({ ...parsed, minute: m });
  const setMeridiem = md => emit({ ...parsed, meridiem: md });

  const displayLabel = parsed.valid
    ? `${parsed.hour}:${pad(parsed.minute)} ${parsed.meridiem}`
    : '';
  const displayHeader = parsed.valid
    ? `${pad(parsed.hour)} : ${pad(parsed.minute)} ${parsed.meridiem}`
    : '-- : -- --';

  return (
    <>
      <Popover
        open={open && !disabled}
        onClose={() => setOpen(false)}
        trigger={
          <TriggerButton
            type="button"
            onClick={() => !disabled && setOpen(v => !v)}
            disabled={disabled}
            $open={open}
            $disabled={!!disabled}
            $error={!!error}
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <ClockIcon />
            <span className={`value${parsed.valid ? '' : ' placeholder'}`}>
              {parsed.valid ? displayLabel : 'Select time'}
            </span>
            <ChevIcon />
          </TriggerButton>
        }
        placement="bottom-start"
      >
        <Tpick>
          <TpickDisplay>{displayHeader}</TpickDisplay>
          <TpickCols>
            <TpickCol ref={hoursColRef}>
              {HOURS.map(h => (
                <TpickCell
                  key={h}
                  type="button"
                  $active={h === parsed.hour}
                  data-active={h === parsed.hour ? 'true' : undefined}
                  onClick={() => setHour(h)}
                >
                  {pad(h)}
                </TpickCell>
              ))}
            </TpickCol>
            <TpickCol ref={minutesColRef}>
              {MINUTES.map(m => (
                <TpickCell
                  key={m}
                  type="button"
                  $active={m === parsed.minute}
                  data-active={m === parsed.minute ? 'true' : undefined}
                  onClick={() => setMinute(m)}
                >
                  {pad(m)}
                </TpickCell>
              ))}
            </TpickCol>
            <TpickCol>
              {MERIDIEMS.map(md => (
                <TpickCell
                  key={md}
                  type="button"
                  $active={md === parsed.meridiem}
                  onClick={() => setMeridiem(md)}
                >
                  {md}
                </TpickCell>
              ))}
            </TpickCol>
          </TpickCols>
        </Tpick>
      </Popover>
      {error && <FieldError>{error}</FieldError>}
    </>
  );
};

export default TimeField;
